/**
 * ============================================================================
 * SIMPLE CHART COMPONENT - 100% Ready
 * ============================================================================
 * 
 * CSS-based simple bar/line chart component (no external dependencies)
 */

import React from 'react';
import Card from '../common/Card';
import { useI18n } from '../../providers';

/**
 * Simple Chart Component
 * @param {Object} props
 * @param {string} props.title - Chart title
 * @param {Array} props.data - Chart data array [{label, value, color?}]
 * @param {string} props.type - Chart type (bar, line)
 * @param {string} props.height - Chart height (default: '200px')
 * @param {boolean} props.embed - If true, render without Card wrapper (for embedding in dashboard-wt-card)
 * @returns {JSX.Element}
 */
const SimpleChart = ({ title, data = [], type = 'bar', height = '200px', embed = false }) => {
  const { language } = useI18n();

  if (!data || data.length === 0) {
    if (embed) {
      return (
        <p className="text-center text-[#a3a3a3] py-8">
          {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
        </p>
      );
    }
    return (
      <Card variant="glass" className="p-8">
        <p className="text-center text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
        </p>
      </Card>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value || 0));
  const content = (
    <div className="space-y-4">
      {title && (
        <h3 className="text-lg font-black text-[#e5e5e5]">{title}</h3>
      )}
      <div className="relative" style={{ height }}>
          {type === 'bar' ? (
            <div className="flex items-end justify-between gap-2 h-full">
              {data.map((item, index) => {
                const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                const color = item.color || 'from-blue-500 to-cyan-500';
                return (
                  <div
                    key={index}
                    className="flex-1 flex flex-col items-center gap-2 animate-fadeInUp"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="relative w-full h-full flex items-end">
                      <div
                        className={`w-full bg-gradient-to-t ${color} rounded-t-lg shadow-lg transition-all duration-500 hover:shadow-xl`}
                        style={{ height: `${percentage}%` }}
                        title={`${item.label}: ${item.value}`}
                      />
                    </div>
                    {item.label && (
                      <p className="text-xs font-medium text-[#a3a3a3] text-center truncate w-full">
                        {item.label}
                      </p>
                    )}
                    {item.value !== undefined && (
                      <p className="text-xs font-bold text-[#e5e5e5]">
                        {item.value}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="relative h-full">
              <svg className="w-full h-full" viewBox={`0 0 ${data.length * 50} 200`}>
                <polyline
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="3"
                  points={data
                    .map(
                      (item, index) =>
                        `${index * 50 + 25},${200 - (item.value / maxValue) * 180}`
                    )
                    .join(' ')}
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
                {data.map((item, index) => (
                  <circle
                    key={index}
                    cx={index * 50 + 25}
                    cy={200 - (item.value / maxValue) * 180}
                    r="4"
                    fill="#3b82f6"
                  />
                ))}
              </svg>
            </div>
          )}
        </div>
      </div>
  );

  if (embed) return content;
  return (
    <Card variant="glass" className="animate-fadeInUp">
      {content}
    </Card>
  );
};

export default SimpleChart;
